import http from "@/utils/axios";
import { notify } from "@kyvg/vue3-notification";
import {
  getFromLocalStorage,
  removeFromLocalStorage,
  saveToLocalStorage,
} from "@/utils/localstorage";

const notifyConf = {
  duration: 5000,
};

const cleanNotify = () => {
  notify({
    clean: true,
  });
};

export default {
  namespaced: true,
  state: {
    currentUser: null,
    address: null,
    addresses: [],
    childrens: [],
    doctors: [],
    facilities: [],
    billingData: null,
    invoicesData: [],
    results: [],
    errors: [],
    stats: {
      referrals: {
        issued: 0,
        paid: 0,
        canceled: 0,
        completed: 0,
      },
      commission: 0,
    },
    toPay: 0,
    quiz: getFromLocalStorage("user/quiz", null),
    quizData: null,
  },
  getters: {
    getQuizData: (state) => state.quizData,
    getQuiz: (state) => state.quiz,
    getCurrentUser: (state) => state.currentUser,
    getAddress: (state) => state.address,
    getAddresses: (state) => state.addresses,
    hasAddresses: (state) => state.addresses.length > 0,
    getDoctors: (state) => state.doctors,
    doctorsAskedForAccess: (state) =>
      state.doctors.filter((doctor) => doctor.ask_for_examinations),
    getInvoicesData: (state) => state.invoicesData,
    getResults: (state) => state.results,
    getCurrentUserRole: (state) => state.currentUser?.role,
    getCurrentUserVerified: (state) => state.currentUser?.sms_verified,
    hasAnyWeightLossProgram: (state) =>
      state.currentUser?.has_any_weight_loss_program,
    isClassifiedForMedication: (state) =>
      state.currentUser?.is_classified_for_medication,
    hasActiveWeightLossProgram: (state) =>
      state.currentUser?.has_active_weight_loss_program,
    activeWeightLossProgramStatus: (state) =>
      state.currentUser?.active_weight_loss_program_status,
    getCurrentUserBillingData: (state) => state.billingData,
    getErrors: (state) => state.errors,
  },
  mutations: {
    setQuiz(state, quiz) {
      state.quiz = quiz;
      saveToLocalStorage("user/quiz", state.quiz);
    },
    setQuizData(state, quiz) {
      state.quizData = quiz;
    },
    clearQuiz(state) {
      state.quiz = null;
      removeFromLocalStorage("user/quiz");
    },
    setCurrentUser(state, user) {
      state.currentUser = user;
    },
    setAddress(state, address) {
      state.address = address;
    },
    setAddresses(state, addresses) {
      state.addresses = addresses;
    },
    pushAddress(state, address) {
      state.addresses = [...state.addresses, address];
    },
    removeAddresses(state, id) {
      state.addresses = state.addresses.filter((address) => address.id != id);
    },
    setChildrens(state, childrens) {
      state.childrens = childrens;
    },
    setDoctors(state, doctors) {
      state.doctors = doctors;
    },
    pushDoctors(state, doctor) {
      state.doctors.push(doctor);
    },
    updateDoctor(state, { index, data }) {
      state.doctors[index] = { ...state.doctors[index], ...data };
    },
    setFacilities(state, facilities) {
      state.facilities = facilities;
    },
    setDefaultFacilitie(state, id) {
      state.facilities = state.facilities.map((facility) => {
        return { ...facility, is_default: facility.id === id };
      });
    },
    pushFacility(state, facility) {
      state.facilities = [...state.facilities, facility];
    },
    removeCollectionPoint(state, id) {
      state.facilities = state.facilities.filter(
        (facility) => facility.id != id
      );
    },
    setInvoicesData(state, invoicesData) {
      state.invoicesData = invoicesData;
    },
    setResults(state, results) {
      state.results = results;
    },
    pushInvoiceData(state, invoiceData) {
      state.invoicesData = [...state.invoicesData, invoiceData];
    },
    updateInvoiceData(state, invoiceData) {
      const index = state.invoicesData.findIndex(
        ({ id }) => invoiceData.id === id
      );

      state.invoicesData[index] = {
        ...state.invoicesData[index],
        ...invoiceData,
      };
    },
    removeInvoiceData(state, id) {
      state.invoicesData = state.invoicesData.filter((data) => data.id != id);
    },
    setUserBillingData(state, billingData) {
      state.billingData = billingData;
    },
    setCurrentUserStats(state, stats) {
      state.stats = stats;
    },
    setUserStats(state, stats) {
      state.stats = stats;
    },
    resetCurrentUser(state) {
      state.currentUser = null;
      state.billingData = null;
    },
    setErrors(state, errors) {
      state.errors = errors;
    },
    clearErrors(state) {
      state.errors = [];
      cleanNotify();
    },
  },
  actions: {
    async assignQuizToUser({ commit, state }) {
      if (state.quiz) {
        try {
          const { data } = await http.post("/user/assign-quiz", state.quiz);
          commit("setCurrentUser", data.user);
          commit("clearQuiz");
        } catch ({ response }) {
          commit("clearQuiz");
        }
      }
    },
    async storeAddress({ commit }, payload) {
      try {
        commit("clearErrors");
        const { data } = await http.post("/profile/address", payload);
        commit("pushAddress", data.address);

        notify({
          type: "success",
          text: "messages.save.success",
          ...notifyConf,
        });
        return data;
      } catch ({ response }) {
        if (response && response.status === 422) {
          commit("setErrors", response.data.errors);
        }

        notify({
          type: "danger",
          text: "messages.save.failure",
          ...notifyConf,
        });

        throw new Error();
      }
    },
    async updateAddress({ commit }, { id, data }) {
      commit("clearErrors");

      try {
        await http.put(`/profile/address/${id}`, data);

        notify({
          type: "success",
          text: "messages.edit.success",
          ...notifyConf,
        });
      } catch ({ response }) {
        if (response && response.status === 422)
          commit("setErrors", response.data.errors);

        notify({
          type: "danger",
          text: "messages.edit.failure",
          ...notifyConf,
        });

        throw new Error();
      }
    },
    async deleteAddress({ commit }, id) {
      try {
        await http.delete(`/profile/address/${id}`);
        commit("removeAddresses", id);

        notify({
          type: "success",
          text: "messages.delete.success",
          ...notifyConf,
        });
      } catch (error) {
        notify({
          type: "success",
          text: "messages.delete.failure",
          ...notifyConf,
        });
      }
    },
    async storeDefaultCollectionPoint({ commit }, id) {
      try {
        commit("clearErrors");
        await http.post("/profile/collection-point/default", {
          id,
        });

        commit("setDefaultFacilitie", id);

        notify({
          type: "success",
          text: "messages.save.success",
          ...notifyConf,
        });
      } catch ({ response }) {
        if (response && response.status === 422) {
          commit("setErrors", response.data.errors);
        }

        notify({
          type: "danger",
          text: "messages.save.failure",
          ...notifyConf,
        });

        throw new Error();
      }
    },
    async shareExaminationsForDoctor({ commit, dispatch }, { id, addAccess }) {
      try {
        commit("clearErrors");
        const { data } = await http.put(`/profile/history/add-access/${id}`, {
          add_access: addAccess,
        });

        dispatch("updateOrCreateDoctor", data);

        notify({
          type: "success",
          text: "messages.save.success",
          ...notifyConf,
        });
      } catch ({ response }) {
        if (response && response.status === 422) {
          commit("setErrors", response.data.errors);
        }

        notify({
          type: "danger",
          text: "messages.save.failure",
          ...notifyConf,
        });

        throw new Error();
      }
    },
    async deleteCollectionPoint({ commit }, id) {
      try {
        await http.delete(`/profile/collection-point/${id}`);
        commit("removeCollectionPoint", id);

        notify({
          type: "success",
          text: "messages.delete.success",
          ...notifyConf,
        });
      } catch (error) {
        notify({
          type: "success",
          text: "messages.delete.failure",
          ...notifyConf,
        });
      }
    },
    async storeInvoiceData({ commit }, payload) {
      try {
        commit("clearErrors");
        const { data } = await http.post("/profile/invoice-data", payload);
        commit("pushInvoiceData", data.data);

        notify({
          type: "success",
          text: "messages.save.success",
          ...notifyConf,
        });
      } catch ({ response }) {
        if (response && response.status === 422) {
          commit("setErrors", response.data.errors);
        }

        notify({
          type: "danger",
          text: "messages.save.failure",
          ...notifyConf,
        });

        throw new Error();
      }
    },
    async updateInvoiceData({ commit }, { id, data }) {
      commit("clearErrors");

      try {
        const { data: resData } = await http.put(
          `/profile/invoice-data/${id}`,
          data
        );

        commit("updateInvoiceData", resData.data);

        notify({
          type: "success",
          text: "messages.edit.success",
          ...notifyConf,
        });
      } catch ({ response }) {
        if (response && response.status === 422)
          commit("setErrors", response.data.errors);

        notify({
          type: "danger",
          text: "messages.edit.failure",
          ...notifyConf,
        });

        throw new Error();
      }
    },
    async deleteInvoiceData({ commit }, id) {
      try {
        await http.delete(`/profile/invoice-data/${id}`);
        commit("removeInvoiceData", id);

        notify({
          type: "success",
          text: "messages.delete.success",
          ...notifyConf,
        });
      } catch (error) {
        notify({
          type: "success",
          text: "messages.delete.failure",
          ...notifyConf,
        });
      }
    },
    storeAndCheckDoctors({ commit, getters }, doctors) {
      commit("setDoctors", doctors);

      if (getters.doctorsAskedForAccess.length > 0) {
        commit("modals/openModal", "doctorAskForAccessToHistoryModal", {
          root: true,
        });
      }
    },
    updateOrCreateDoctor({ state, commit }, doctor) {
      const doctorIndex = state.doctors.findIndex(({ id }) => doctor.id === id);

      if (doctorIndex >= 0) {
        commit("updateDoctor", { index: doctorIndex, data: doctor });
      } else {
        commit("pushDoctors", doctor);
      }
    },
    async fetchAccountData({ commit, dispatch, rootGetters }) {
      try {
        const { data } = await http.get("/account-data");
        const firstOrDefaultAddress = getDefaultOrFirst(data.addresses);
        commit("setAddresses", data.addresses);
        commit("setChildrens", data.childrens);
        commit("setFacilities", data.facilities);
        commit("setInvoicesData", data.invoicesData);
        commit("setAddress", firstOrDefaultAddress);
        dispatch("storeAndCheckDoctors", data.doctors);

        if (!rootGetters["checkout/getSelectedAddressId"]) {
          commit("checkout/setSelectedAddressId", firstOrDefaultAddress?.id, {
            root: true,
          });
          commit("cart/setSelectedCity", firstOrDefaultAddress?.city_model, {
            root: true,
          });
        }
      } catch (e) {
        console.log(e);
      }
    },
    async fetchCurrentUser({ commit, dispatch, rootGetters }) {
      const response = await http.get("/user");
      const user = response.data.user;

      commit("setCurrentUser", user);

      commit("billingData/setCheckAll", false, { root: true });

      if (user.billing_data) {
        commit("setUserBillingData", user.billing_data);
        commit("billingData/setBillingData", user.billing_data, { root: true });
      }
      if (user.role === "doctor") {
        commit("setUserStats", response.data.stats);
        commit(
          "cart/setPatientDiscountPercent",
          user.patient_discount_percent,
          { root: true }
        );
      }

      await dispatch("fetchAccountData");

      if (!rootGetters["cart/getSelectedFacility"])
        dispatch("cart/setUserDefaultFacility", user.default_facility, {
          root: true,
        });
    },
    async updateCurrentUser({ commit, dispatch }, user) {
      return http
        .post(`/profile/update`, user)
        .then((response) => {
          const user = response.data.user;
          commit("setCurrentUser", user);
          dispatch(
            "checkout/updatePatientById",
            { id: user.id, patient: user },
            { root: true }
          );
          commit("clearErrors");
          notify({
            type: "success",
            text: "messages.edit.success",
            duration: 5000,
          });
        })
        .catch(({ response }) => {
          if (response && response.status === 422) {
            commit("setErrors", response.data.errors);
          } else if (response && response.status === 403) {
            if (response.data.message == "error.current_password.incorrect") {
              commit("setErrors", {
                current_password: [response.data.message],
              });
            } else if (response.data.message == "error.code.incorrect") {
              commit("setErrors", {
                code: [response.data.message],
              });
            }
          }

          notify({
            type: "danger",
            text: "messages.edit.failure",
            ...notifyConf,
          });

          throw new Error();
        });
    },
    async isRegisterUser(_, payload) {
      return await http.post("/check/user/exists", payload);
    },
    async fetchResults({ commit }, params = {}) {
      const { data } = await http.get("/profile/results", { params });
      commit("setResults", data.data);
    },
    async fetchQuiz({ commit }) {
      const { data } = await http.get("/quizzes/current");

      commit("setQuizData", data.data);
    },
  },
};

const getDefaultOrFirst = (addresses) => {
  const defaultAddress = addresses.find((address) => address.is_default);

  return defaultAddress || addresses[0] || null;
};
